import React, { useEffect, useState } from "react";
import {
  ShieldCheck,
  AlertTriangle,
  Activity,
  Sparkles,
  ScrollText,
  ClipboardCheck,
  Users2,
  GraduationCap,
  Award,
  Target,
  Briefcase,
  CalendarClock,
} from "lucide-react";
import { PageHeader } from "../layout/Shell";
import { Badge } from "../ui/badge";
import { Kpi, Panel, ItemList, PageLink } from "./widgets";
import { api } from "../../lib/api";

// ---------------------------------------------------------------------------
// Compliance Officer — owns audit trails + framework compliance
// ---------------------------------------------------------------------------
export function ComplianceOfficerDashboard({ inst }) {
  const [audit, setAudit] = useState([]);
  useEffect(() => {
    if (!inst) return;
    api.get(`/audit/${inst.id}`, { params: { limit: 8 } })
      .then((r) => setAudit(r.data?.items || r.data || []))
      .catch(() => setAudit([]));
  }, [inst?.id]);

  return (
    <div data-testid="dashboard-compliance">
      <PageHeader
        eyebrow="Audit & Governance"
        title={`${inst.short_name} · Compliance Officer`}
        description={`Framework: ${inst.compliance_framework || "—"} · Residency: ${inst.data_residency || "—"}`}
        actions={<Badge className="bg-primary text-primary-foreground">Compliance Officer</Badge>}
      />
      <div className="p-6 lg:p-8 space-y-8">
        <section className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <Kpi label="Audit events · 7d" value={audit.length} icon={Activity} testid="comp-kpi-events" />
          <Kpi label="Open alerts" value="—" icon={AlertTriangle} testid="comp-kpi-alerts" />
          <Kpi label="Framework score" value="—" icon={ShieldCheck} testid="comp-kpi-framework" />
          <Kpi label="HITL gates" value="—" icon={ClipboardCheck} testid="comp-kpi-hitl" />
        </section>

        <section className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          <Panel eyebrow="Inbox" title="Recent audit events" testid="comp-events" className="lg:col-span-2"
                 action={<PageLink to="/compliance" label="Open explorer" />}>
            <ItemList
              testid="comp-events-list"
              empty="No audit events recorded yet."
              items={audit.slice(0, 6).map((e) => ({
                id: e.id,
                title: e.action,
                meta: `${e.actor} · ${e.target || "—"}`,
                right: new Date(e.ts).toLocaleString([], { dateStyle: "short", timeStyle: "short" }),
              }))}
            />
          </Panel>
          <Panel eyebrow="Alerts" title="Open investigations" testid="comp-investigations">
            <ItemList items={[]} empty="No open investigations." />
          </Panel>
        </section>

        <section className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <Panel eyebrow="Compliance posture" title={`${inst.compliance_framework || "Framework"} controls`} testid="comp-controls">
            <div className="py-6 text-center text-xs text-muted-foreground">No compliance data available.</div>
          </Panel>
          <Panel eyebrow="Workflows" title="Compliance-relevant" testid="comp-workflows" action={<PageLink to="/workflows" label="Open workflows" />}>
            <ItemList items={[]} empty="No workflow data available." />
          </Panel>
        </section>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// AI Governance Admin — owns prompt-policy + bias audits
// ---------------------------------------------------------------------------
export function AIGovernanceDashboard({ inst }) {
  const [cases, setCases] = useState([]);
  useEffect(() => {
    if (!inst) return;
    api.get(`/ai/use-cases/${inst.id}`).then((r) => setCases(r.data || [])).catch(() => setCases([]));
  }, [inst?.id]);

  const active = cases.filter((c) => c.status === "active").length;
  const pending = cases.filter((c) => c.status === "coming_soon").length;

  return (
    <div data-testid="dashboard-ai-governance">
      <PageHeader
        eyebrow="AI TRiSM"
        title={`${inst.short_name} · AI Governance`}
        description="Prompt-policy approvals, bias audits and human-in-the-loop coverage."
        actions={
          <>
            <Badge variant="outline" className="gap-1.5"><Sparkles className="h-3 w-3" /> Universal Key</Badge>
            <Badge className="bg-primary text-primary-foreground">AI Governance Admin</Badge>
          </>
        }
      />
      <div className="p-6 lg:p-8 space-y-8">
        <section className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <Kpi label="Active use-cases" value={active} icon={Sparkles} testid="aig-kpi-active" />
          <Kpi label="Pending approval" value={pending} icon={ClipboardCheck} testid="aig-kpi-pending" />
          <Kpi label="Prompt policy" value="—" icon={ScrollText} testid="aig-kpi-policy" />
          <Kpi label="Bias audit score" value="—" icon={ShieldCheck} testid="aig-kpi-bias" />
        </section>

        <section className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          <Panel eyebrow="Queue" title="Prompt-policy approvals" testid="aig-queue" className="lg:col-span-2"
                 action={<PageLink to="/governance" label="Open governance" />}>
            <ItemList
              testid="aig-queue-list"
              items={cases.slice(0, 6).map((c) => ({
                id: c.id,
                title: `${c.code} · ${c.name_en}`,
                meta: `Risk score ${c.risk_score ?? "—"} · HITL ${c.hitl_required ? "ON" : "OFF"} · Citations ${c.citations_required ? "ON" : "OFF"}`,
                right: c.status,
              }))}
              empty="No pending policy approvals."
            />
          </Panel>
          <Panel eyebrow="HITL coverage" title="Gates enforced" testid="aig-hitl">
            <div className="py-6 text-center text-xs text-muted-foreground">No HITL data available.</div>
          </Panel>
        </section>

        <Panel eyebrow="Recent" title="Governance signal" testid="aig-signal">
          <ItemList items={[]} empty="No governance events recorded yet." />
        </Panel>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Training Manager — owns delivery cadence & instructor allocation
// ---------------------------------------------------------------------------
export function TrainingManagerDashboard({ inst, m }) {
  return (
    <div data-testid="dashboard-training-manager">
      <PageHeader
        eyebrow="Training Operations"
        title={`${inst.short_name} · Training Manager`}
        description="Delivery cadence, instructor allocation and certification flow."
        actions={<Badge className="bg-primary text-primary-foreground">Training Manager</Badge>}
      />
      <div className="p-6 lg:p-8 space-y-8">
        <section className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <Kpi label="Active programmes" value={m.programmes ?? "—"} icon={GraduationCap} testid="tm-kpi-programmes" />
          <Kpi label="Active learners" value={(m.learners ?? m.students)?.toLocaleString() ?? "—"} icon={Users2} testid="tm-kpi-learners" />
          <Kpi label="Instructors deployed" value={m.instructors ?? m.faculty ?? "—"} icon={ClipboardCheck} testid="tm-kpi-instructors" />
          <Kpi label="Cert compliance" value={m.certification_compliance != null ? `${m.certification_compliance}%` : m.completion_rate != null ? `${m.completion_rate}%` : "—"} icon={Award} testid="tm-kpi-cert" />
        </section>

        <section className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          <Panel eyebrow="This week" title="Sessions delivered" testid="tm-sessions" className="lg:col-span-2">
            <ItemList items={[]} empty="No session data available." />
          </Panel>
          <Panel eyebrow="Allocation" title="Instructor load" testid="tm-load">
            <div className="py-6 text-center text-xs text-muted-foreground">No instructor data available.</div>
          </Panel>
        </section>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// HR / Workforce Planner — owns workforce readiness & skill gaps
// ---------------------------------------------------------------------------
export function HRWorkforceDashboard({ inst, m }) {
  return (
    <div data-testid="dashboard-hr-workforce">
      <PageHeader
        eyebrow="Workforce Planning"
        title={`${inst.short_name} · HR · Workforce Planner`}
        description="Skill gaps, certification expiries and workforce readiness."
        actions={<Badge className="bg-primary text-primary-foreground">HR Workforce Planner</Badge>}
      />
      <div className="p-6 lg:p-8 space-y-8">
        <section className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <Kpi label="Workforce readiness" value={m.workforce_readiness != null ? `${m.workforce_readiness}%` : "—"} icon={Target} testid="hr-kpi-readiness" />
          <Kpi label="Active learners" value={(m.learners ?? m.students)?.toLocaleString() ?? "—"} icon={Users2} testid="hr-kpi-learners" />
          <Kpi label="Certs expiring · 90d" value={m.expiring_certs ?? "—"} icon={CalendarClock} testid="hr-kpi-expiring" />
          <Kpi label="Skill gaps · critical" value="—" icon={AlertTriangle} testid="hr-kpi-gaps" />
        </section>

        <section className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <Panel eyebrow="Skill heatmap" title="Critical gaps" testid="hr-skill-heatmap">
            <div className="py-6 text-center text-xs text-muted-foreground">No skill gap data available.</div>
          </Panel>
          <Panel eyebrow="Expiring certifications" title="Next 90 days" testid="hr-expiring">
            <ItemList items={[]} empty="No expiring certifications." />
          </Panel>
        </section>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Line Manager — owns own team readiness
// ---------------------------------------------------------------------------
export function LineManagerDashboard({ inst }) {
  return (
    <div data-testid="dashboard-line-manager">
      <PageHeader
        eyebrow="Team Operations"
        title={`${inst.short_name} · Line Manager`}
        description="Your reports' readiness, certifications and active assignments."
        actions={<Badge className="bg-primary text-primary-foreground">Line Manager</Badge>}
      />
      <div className="p-6 lg:p-8 space-y-8">
        <section className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <Kpi label="Direct reports" value="—" icon={Users2} testid="lm-kpi-team" />
          <Kpi label="Team readiness" value="—" icon={Target} testid="lm-kpi-readiness" />
          <Kpi label="Certs expiring · 30d" value="—" icon={CalendarClock} testid="lm-kpi-expiring" />
          <Kpi label="Open dev plans" value="—" icon={ClipboardCheck} testid="lm-kpi-plans" />
        </section>

        <section className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <Panel eyebrow="Team" title="Readiness by report" testid="lm-team">
            <div className="py-6 text-center text-xs text-muted-foreground">No team readiness data available.</div>
          </Panel>
          <Panel eyebrow="This month" title="Active assignments" testid="lm-assignments">
            <ItemList items={[]} empty="No active assignments." />
          </Panel>
        </section>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Executive Leadership — strategic top-line
// ---------------------------------------------------------------------------
export function ExecutiveDashboard({ inst, m }) {
  return (
    <div data-testid="dashboard-executive">
      <PageHeader
        eyebrow="Executive Brief"
        title={`${inst.short_name} · Leadership Cockpit`}
        description={`${inst.country} · ${inst.compliance_framework || "—"}`}
        actions={
          <>
            <Badge variant="outline" className="gap-1.5">
              <span className="dot-pulse" />
              {(m.ai_sessions ?? 0).toLocaleString()} AI sessions
            </Badge>
            <Badge className="bg-primary text-primary-foreground">Executive Leadership</Badge>
          </>
        }
      />
      <div className="p-6 lg:p-8 space-y-8">
        <section className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <Kpi label={m.learners ? "Active learners" : "Students"} value={(m.learners ?? m.students)?.toLocaleString() ?? "—"} icon={Users2} testid="exec-kpi-learners" />
          <Kpi label="Workforce readiness" value={m.workforce_readiness != null ? `${m.workforce_readiness}%` : "—"} icon={Target} testid="exec-kpi-readiness" />
          <Kpi label="AI sessions" value={m.ai_sessions?.toLocaleString() ?? "—"} icon={Sparkles} testid="exec-kpi-ai" />
          <Kpi label="Compliance" value={m.certification_compliance != null ? `${m.certification_compliance}%` : m.completion_rate != null ? `${m.completion_rate}%` : "—"} icon={ShieldCheck} testid="exec-kpi-compliance" />
        </section>

        <section className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <Panel eyebrow="Strategic highlights" title="Last 30 days" testid="exec-highlights">
            <ItemList items={[]} empty="No highlights data available." />
          </Panel>
          <Panel eyebrow="Risks" title="Watch list" testid="exec-risks" action={<PageLink to="/compliance" label="Open audit" />}>
            <ItemList items={[]} empty="No risk signals detected." />
          </Panel>
        </section>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Faculty — focused on courses & content drafts
// ---------------------------------------------------------------------------
export function FacultyDashboard({ inst, m }) {
  return (
    <div data-testid="dashboard-faculty">
      <PageHeader
        eyebrow="Teaching Workspace"
        title={`${inst.short_name} · Faculty`}
        description="Your active courses, content drafts and examiner reports."
        actions={<Badge className="bg-primary text-primary-foreground">Faculty</Badge>}
      />
      <div className="p-6 lg:p-8 space-y-8">
        <section className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <Kpi label="Active courses" value="—" icon={GraduationCap} testid="fac-kpi-courses" />
          <Kpi label="Learners taught" value="—" icon={Users2} testid="fac-kpi-learners" />
          <Kpi label="Drafts pending" value="—" icon={ClipboardCheck} testid="fac-kpi-drafts" />
          <Kpi label="Examiner score" value="—" icon={Award} testid="fac-kpi-examiner" />
        </section>
        <section className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <Panel eyebrow="Drafts" title="In Content Studio" testid="fac-drafts" action={<PageLink to="/content-studio" label="Open studio" />}>
            <ItemList items={[]} empty="No drafts in progress." />
          </Panel>
          <Panel eyebrow="AI Instructor" title="This week's sessions" testid="fac-instructor" action={<PageLink to="/ai-instructor" label="Open instructor" />}>
            <ItemList items={[]} empty="No session data available." />
          </Panel>
        </section>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Student — learner-side first screen
// ---------------------------------------------------------------------------
export function StudentDashboard({ inst, user }) {
  const [tickets, setTickets] = useState([]);
  useEffect(() => {
    if (!inst) return;
    api.get(`/tickets/${inst.id}`).then((r) => setTickets(r.data || [])).catch(() => setTickets([]));
  }, [inst?.id]);
  return (
    <div data-testid="dashboard-student">
      <PageHeader
        eyebrow="Learning Hub"
        title={`${user?.name?.split(" ")[0] || "Hello"} · ${inst.short_name}`}
        description={user?.title || "Your courses, next steps and AI helpers."}
        actions={<Badge className="bg-primary text-primary-foreground">Student</Badge>}
      />
      <div className="p-6 lg:p-8 space-y-8">
        <section className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <Kpi label="Active courses" value="—" icon={GraduationCap} testid="stu-kpi-courses" />
          <Kpi label="Assessments pending" value="—" icon={ClipboardCheck} testid="stu-kpi-assess" />
          <Kpi label="Career readiness" value="—" icon={Briefcase} testid="stu-kpi-career" />
          <Kpi label="Open tickets" value={tickets.length} icon={ScrollText} testid="stu-kpi-tickets" />
        </section>
        <section className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <Panel eyebrow="Next 7 days" title="Coming up" testid="stu-upcoming">
            <ItemList items={[]} empty="No upcoming items." />
          </Panel>
          <Panel eyebrow="AI helpers" title="Use what you need" testid="stu-helpers">
            <ItemList
              items={[
                { title: "Ask AI Instructor", meta: "Course-grounded answers", right: "→" },
                { title: "Get a career plan", meta: "AI Advisor", right: "→" },
                { title: "Open a support ticket", meta: "Student Assistant", right: "→" },
              ]}
            />
          </Panel>
        </section>
      </div>
    </div>
  );
}
